/* ds04 #68: 日本の住所の入力補助。会員フォーム面（layout/theme.liquid の tol_is_account_form）で読む。
   郵便番号から 都道府県・市区町村・住所1 を入れる（zipcloud）。旧 LIVE は sections/popup-group.json の
   AI 生成ブロック（ai_gen_block_4b69b2e）で会員情報入力（helium の default_address.* の欄）に同じ機能を
   持っていた＝新テーマで受け皿が無く消えていた退行の復元（DECISIONS 2026-10-08（8））。
   旧 LIVE のスクリプトから変えた点:
   - フォームごとに結ぶ（旧は最初の 1 欄だけ）・古い応答で上書きしない（郵便番号を A→B と変えて A の
     応答が後から来ても捨てる）・国が日本のときだけ・郵便番号が読み込み時（または前回の検索）から
     変わったときだけ検索（欄を Tab で通るだけでは動かない）・住所1 は空のときだけ入れる。
   - 都道府県は表示名でなく zipcloud の prefcode から値を引く（表示名はロケールで変わる）。
   郵便番号は zipcloud（第三者の API・旧 LIVE と同じ相手）へ送る。 */
(() => {
  // JIS X 0401 の都道府県コード 1〜47 の順＝Shopify の Japan の province の値（helium の select も同じ値・同じ順）
  const PREFECTURES = [
    'Hokkaidō', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima', 'Ibaraki', 'Tochigi', 'Gunma',
    'Saitama', 'Chiba', 'Tōkyō', 'Kanagawa', 'Niigata', 'Toyama', 'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano',
    'Gifu', 'Shizuoka', 'Aichi', 'Mie', 'Shiga', 'Kyōto', 'Ōsaka', 'Hyōgo', 'Nara', 'Wakayama',
    'Tottori', 'Shimane', 'Okayama', 'Hiroshima', 'Yamaguchi', 'Tokushima', 'Kagawa', 'Ehime', 'Kōchi', 'Fukuoka',
    'Saga', 'Nagasaki', 'Kumamoto', 'Ōita', 'Miyazaki', 'Kagoshima', 'Okinawa',
  ];
  const API = 'https://zipcloud.ibsnet.co.jp/api/search?zipcode=';
  // 会員情報入力（helium）と、お届け先の新規・編集フォーム（旧 LIVE には無かった＝新しい依頼）
  const ZIP_SELECTOR = 'input[name="default_address.zip"], [data-customer-addresses] input[name="address[zip]"]';
  const bound = new WeakSet();

  const digits = (value) =>
    String(value || '')
      .replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0))
      .replace(/[^0-9]/g, '');

  // helium のフォームは React の制御された入力（_valueTracker あり）。el.value = … だと React の
  // 値の追跡も同じ値に書き換わり、input を出しても変化と見なされない＝画面だけ変わって保存されるのは
  // 古い値になる。標準の value の setter を通してからイベントを出す（お届け先の素のフォームでも同じに効く）。
  const setValue = (el, value) => {
    const proto = el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // 複数件のとき町域の共通の先頭だけを入れる
  const commonPrefix = (list) =>
    list.reduce((prefix, item) => {
      let i = 0;
      while (i < prefix.length && i < item.length && prefix[i] === item[i]) i++;
      return prefix.slice(0, i);
    });

  const fieldsOf = (zip) => {
    const form = zip.form || zip.closest('form');
    if (!form) return null;
    const field = (key) => form.querySelector(`[name="address[${key}]"], [name="default_address.${key}"]`);
    return { form, country: field('country'), province: field('province'), city: field('city'), address1: field('address1') };
  };

  const isJapan = (fields) => !fields.country || fields.country.value === 'Japan';

  const bind = (zip) => {
    if (bound.has(zip)) return;
    bound.add(zip);
    // done = 調べ終えた（または読み込み時・キャンセル後の）番号＝同じ番号では調べ直さない。
    // pending = 問い合わせ中の番号。捨てた問い合わせと失敗した問い合わせの番号は done に入れない
    // （入れ直せばもう一度調べられる）。
    let done = digits(zip.value);
    let pending = null;
    let seq = 0;
    let filledAddress1 = null; // 直前に自動で入れた住所1（郵便番号を直したときはこれだけ入れ替えてよい）
    const form = zip.form || zip.closest('form');
    // キャンセル（reset）と国の変更では、待っている応答を捨てる。reset 後は戻った郵便番号を基準にする
    const invalidate = () => {
      seq++;
      pending = null;
    };
    if (form) {
      form.addEventListener('reset', () => {
        invalidate();
        filledAddress1 = null;
        setTimeout(() => (done = digits(zip.value)));
      });
      form.addEventListener('change', (event) => {
        if (event.target && /country\]?$/.test(event.target.name || '')) invalidate();
      });
    }

    const lookup = () => {
      const code = digits(zip.value);
      if (code.length !== 7 || code === done || code === pending) return;
      const fields = fieldsOf(zip);
      if (!fields || !fields.province || !fields.city || !isJapan(fields)) return;
      pending = code;
      const mine = ++seq;
      const settle = () => {
        if (mine === seq) pending = null;
      };
      // 待っている間に手で直された欄は上書きしない
      const startProvince = fields.province.value;
      const startCity = fields.city.value;
      fetch(API + code)
        .then((response) => {
          if (!response.ok) throw new Error(`zipcloud ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (mine !== seq) return;
          settle();
          if (digits(zip.value) !== code || !isJapan(fields)) return;
          if (fields.province.value !== startProvince || fields.city.value !== startCity) {
            done = code; // 手で直した欄を、同じ番号で後から上書きしない
            return;
          }
          done = code;
          const results = data && data.status === 200 && Array.isArray(data.results) ? data.results : [];
          if (results.length === 0) return;
          const value = PREFECTURES[Number(results[0].prefcode) - 1];
          if (!value || ![...fields.province.options].some((option) => option.value === value)) return;
          setValue(fields.province, value);
          setValue(fields.city, results[0].address2);
          // 住所1 は空か、直前に自動で入れたままのときだけ入れる（保存済み・手入力の番地は消さない）。
          // 複数件で町域の共通の先頭が無いときは空にする（旧 LIVE は 1 件目を入れていたが違う町域になりうる・
          // 前の郵便番号で自動で入れた町域も残さない）
          const town = commonPrefix(results.map((result) => result.address3 || ''));
          if (fields.address1 && (!fields.address1.value.trim() || fields.address1.value === filledAddress1)) {
            if (fields.address1.value !== town) setValue(fields.address1, town);
            filledAddress1 = town || null;
          }
        })
        .catch(settle); // 通信の失敗は done に入れない＝同じ番号を入れ直せばもう一度調べる
    };

    zip.addEventListener('input', lookup);
    zip.addEventListener('blur', lookup);
  };

  const scan = () => document.querySelectorAll(ZIP_SELECTOR).forEach(bind);

  // お届け先: 日本の都道府県を北海道〜沖縄の順に（旧 LIVE も Shopify の英語名の順だった＝新しい依頼）。
  // Shopify.CountryProvinceSelector は window.load（sections/main-addresses.liquid）で data-provinces から
  // 組み立てるので、その前に属性の並びを替える＝国を変えて戻したときの組み直しも同じ順になり、保存済みの
  // 値の選択（data-default）もそのまま効く。表に無い値があれば元の順のまま（option を落とさない）。
  const sortJapan = () => {
    const rank = new Map(PREFECTURES.map((value, index) => [value, index]));
    document
      .querySelectorAll('[data-customer-addresses] select[name="address[country]"] option[value="Japan"]')
      .forEach((option) => {
        try {
          const list = JSON.parse(option.getAttribute('data-provinces'));
          if (!Array.isArray(list) || !list.every((province) => rank.has(province[0]))) return;
          list.sort((a, b) => rank.get(a[0]) - rank.get(b[0]));
          option.setAttribute('data-provinces', JSON.stringify(list));
        } catch (error) {}
      });
  };

  // お届け先の新規追加: 都道府県の先頭を「選択してください」に（旧 LIVE は先頭の都道府県が選ばれていた）。
  // Shopify は国が変わるたびに option を作り直すので、作り直しのたびに先頭へ足す。欄が見えているときだけ
  // required（Dawn は form.errors を出さない＝空のまま送ると黙って失敗するか都道府県なしで保存される）。
  const placeholder = () => {
    const select = document.getElementById('AddressProvinceNew');
    if (!select || !select.dataset.tolPlaceholder) return;
    const container = document.getElementById('AddressProvinceContainerNew');
    const country = document.getElementById('AddressCountryNew');
    // 人が国を選び直したか（このリスナーは Shopify の countryHandler より先に登録される＝組み直しの前に立つ）。
    // 読み込み時の組み立てと、キャンセルの当て直し（sections/main-addresses.liquid・合成の change）は含まない。
    let userChangedCountry = false;
    if (country) country.addEventListener('change', (event) => (userChangedCountry = event.isTrusted));
    const apply = () => {
      if (select.options.length === 0) {
        select.required = false;
        return;
      }
      if (select.options[0].hasAttribute('data-tol-placeholder')) return;
      const option = new Option(select.dataset.tolPlaceholder, '');
      option.setAttribute('data-tol-placeholder', '');
      select.prepend(option);
      // 送信に失敗して描き直したとき（data-default に送信した値がある）は、読み込み時とキャンセルの
      // 当て直しでだけその選択を残す。人が国を選び直したら必ず「選択してください」から
      if (!select.dataset.default || userChangedCountry) select.value = '';
      userChangedCountry = false;
      select.required = !container || container.style.display !== 'none';
    };
    new MutationObserver(apply).observe(select, { childList: true });
    apply();
  };

  const start = () => {
    sortJapan();
    placeholder();
    scan();
    // helium（会員登録・会員情報入力）はフォームを後から描く＝その 2 面だけ監視する
    if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:account\/register|tools\/customr\/)/i.test(location.pathname)) return;
    // まとめて 1 回だけ探す。requestAnimationFrame はタブが裏にあると動かないので setTimeout にする
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        scan();
      }, 50);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
